/**
 * CONN-PROBE-4-FE — Histórico de conectividade (timeline LAN/Internet/DNS).
 *
 * Consome GET /api/admin/devices/{id}/conn-history (agrega heartbeats_raw,
 * read-only). Observabilidade APENAS — nada aqui aciona comando/relé/reboot/OTA.
 *
 * Contrato de timeline NÃO ambíguo (do backend F4A):
 *   ponto gap=true (+ alvos null) → SEM heartbeat no bucket   (≠ falha de alvo)
 *   alvo state="missing"          → houve HB, alvo não veio    (≠ falha)
 * A disponibilidade (availability_pct) exclui gap/missing/desconhecido — a UI
 * NUNCA pinta gap/missing como falha nem transforma ausência de RTT em 0.
 *
 * Toda a lógica de classificação/formatação vive em ../lib/connProbe.js (puro,
 * testado em node). Este componente é só apresentação + estados de tela.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getConnHistory } from "../lib/api";
import {
  CONN_AVAILABILITY_HELP,
  CONN_HISTORY_DEFAULT_HOURS,
  CONN_HISTORY_PERIODS,
  CONN_HIST_LEGEND,
  CONN_HIST_STATE,
  connHistCellState,
  connHistCellTitle,
  connHistPointDetail,
  connHistStateMeta,
  connHistTargetSummary,
  formatClockLocal,
  formatPct,
  shouldApplyResponse,
} from "../lib/connProbe.js";

const TARGETS = [
  { key: "lan", label: "Rede local", tip: "Ping ao gateway/LAN do device." },
  { key: "inet", label: "Internet", tip: "Ping a um alvo externo (conectividade externa)." },
  { key: "dns", label: "DNS", tip: "Tempo de resolução de nomes." },
];

const TONE_TEXT = {
  ok: "text-noc-ok",
  warn: "text-noc-warn",
  err: "text-noc-err",
  muted: "text-noc-muted",
};

// Estilo de preenchimento de uma célula da timeline (não depende só de cor:
// gap é hachurado, cada estado tem glyph/label no tooltip e na legenda).
function cellStyle(kind) {
  const meta = connHistStateMeta(kind);
  if (kind === CONN_HIST_STATE.GAP) {
    // hachura diagonal + sem preenchimento sólido = claramente "sem heartbeat".
    return {
      backgroundImage:
        "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(90,115,144,0.45) 2px, rgba(90,115,144,0.45) 3px)",
      border: "1px solid rgba(90,115,144,0.35)",
    };
  }
  if (kind === CONN_HIST_STATE.NODATA) {
    return { backgroundColor: "transparent", border: "1px solid rgba(28,46,73,0.8)" };
  }
  return { backgroundColor: meta.color };
}

// ── seletor de período (segmented control, estilo da barra de abas do NOC) ──
function PeriodSelector({ value, onChange, disabled }) {
  return (
    <div
      role="group"
      aria-label="Período do histórico"
      className="inline-flex rounded-lg border border-noc-border bg-noc-bg/50 p-0.5"
    >
      {CONN_HISTORY_PERIODS.map((p) => {
        const active = p.hours === value;
        return (
          <button
            key={p.key}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(p.hours)}
            className={`px-2.5 py-1 rounded-md font-mono text-[11px] tracking-[0.06em] transition-colors focus:outline-none focus:ring-2 focus:ring-noc-accent/40 disabled:opacity-50 ${
              active
                ? "bg-noc-accent/15 text-noc-accent"
                : "text-noc-faint hover:text-noc-text2"
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

// ── cartão de resumo por alvo ──
function TargetSummaryCard({ label, tip, target, fallbackOkPct }) {
  const s = connHistTargetSummary(target);
  if (!s) {
    return (
      <div className="rounded-lg border border-noc-border/50 bg-noc-bg/40 px-3 py-2">
        <div className="flex items-center justify-between">
          <span title={tip} className="cursor-help text-[11px] font-semibold text-noc-text2">
            {label}
          </span>
          <span className="text-[11px] text-noc-faint">sem dados</span>
        </div>
        <div className="mt-1 text-[10px] text-noc-faint">Não reportado no período.</div>
      </div>
    );
  }
  const availText = s.availability !== "—" ? s.availability : formatPct(fallbackOkPct);
  return (
    <div className="rounded-lg border border-noc-border/50 bg-noc-bg/40 px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span title={tip} className="cursor-help text-[11px] font-semibold text-noc-text2">
          {label}
        </span>
        <span
          title={CONN_AVAILABILITY_HELP}
          className={`cursor-help font-mono text-base font-semibold ${TONE_TEXT[s.tone] || "text-noc-text"}`}
        >
          {availText}
        </span>
      </div>
      <div className="mt-0.5 flex items-center justify-between text-[10px] text-noc-faint">
        <span title={CONN_AVAILABILITY_HELP} className="cursor-help">
          disponibilidade
        </span>
        <span className="font-mono text-noc-muted">amostras OK {s.okPct}</span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1 text-center">
        <MiniStat label="falhas" value={s.failBuckets} tone={s.failBuckets > 0 ? "err" : null} />
        <MiniStat label="parciais" value={s.partialBuckets} tone={s.partialBuckets > 0 ? "warn" : null} />
        <MiniStat label="não report." value={s.missingBuckets} />
      </div>
      {(s.failBuckets > 0 || s.consecutiveFail > 0) && (
        <div className="mt-2 space-y-0.5 border-t border-noc-border/40 pt-1.5 text-[10px] text-noc-muted">
          <div className="flex justify-between">
            <span className="text-noc-faint">pior falha contínua</span>
            <span className="font-mono">{s.longestFailure}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-noc-faint">buckets consecutivos</span>
            <span className="font-mono">{s.consecutiveFail}</span>
          </div>
          {s.firstFailure && (
            <div className="flex justify-between">
              <span className="text-noc-faint">1ª falha</span>
              <span className="font-mono">{s.firstFailure}</span>
            </div>
          )}
          {s.lastFailure && (
            <div className="flex justify-between">
              <span className="text-noc-faint">última falha</span>
              <span className="font-mono">{s.lastFailure}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="rounded bg-noc-panel2/40 px-1 py-0.5">
      <div className={`font-mono text-xs font-semibold ${TONE_TEXT[tone] || "text-noc-text"}`}>
        {value}
      </div>
      <div className="text-[8px] uppercase tracking-[0.08em] text-noc-faint">{label}</div>
    </div>
  );
}

// ── uma faixa (alvo) da timeline: N células, uma por bucket ──
function TimelineLane({ label, tip, targetKey, points, bucketMinutes, activeIdx, onActivate }) {
  return (
    <div className="flex items-center gap-2">
      <span
        title={tip}
        className="w-16 shrink-0 cursor-help font-mono text-[10px] uppercase tracking-[0.08em] text-noc-muted"
      >
        {label}
      </span>
      <div className="flex flex-1 gap-px">
        {points.map((p, i) => {
          const kind = connHistCellState(p, targetKey);
          const title = connHistCellTitle(p, targetKey, label, bucketMinutes);
          const isActive = i === activeIdx;
          return (
            <button
              key={i}
              type="button"
              title={title}
              aria-label={title}
              onMouseEnter={() => onActivate(i)}
              onFocus={() => onActivate(i)}
              onClick={() => onActivate(i)}
              style={cellStyle(kind)}
              className={`h-4 min-w-[3px] flex-1 rounded-[1px] focus:outline-none ${
                isActive ? "ring-2 ring-noc-accent ring-offset-0" : ""
              }`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── faixa de detalhe do ponto sob foco/mouse/toque ──
function DetailStrip({ point, bucketMinutes }) {
  const detail = point ? connHistPointDetail(point, bucketMinutes) : null;
  if (!detail) {
    return (
      <div className="rounded-lg border border-noc-border/40 bg-noc-bg/30 px-3 py-2 text-[11px] text-noc-faint">
        Passe o mouse, use o teclado ou toque em um ponto da linha do tempo para ver o detalhe.
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-noc-border/40 bg-noc-bg/30 px-3 py-2">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] text-noc-text2">{detail.time}</span>
        <span className="font-mono text-[10px] text-noc-faint">Período: {detail.period}</span>
      </div>
      {detail.gap ? (
        <div className="flex items-center gap-2 text-[11px]">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-[1px]"
            style={cellStyle("gap")}
          />
          <span className="font-medium text-noc-muted">Sem heartbeat</span>
          <span className="text-noc-faint">{detail.note}</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {detail.targets.map((t) => {
            const meta = connHistStateMeta(t.kind);
            return (
              <div key={t.key} className="rounded-md bg-noc-panel2/30 px-2 py-1.5">
                <div className="mb-1 flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-[1px]"
                    style={cellStyle(t.kind)}
                  />
                  <span className="text-[10px] uppercase tracking-[0.08em] text-noc-faint">
                    {t.label}
                  </span>
                  <span className={`ml-auto text-[11px] font-medium ${meta.text}`}>
                    {t.stateLabel}
                  </span>
                </div>
                {t.note ? (
                  <div className="text-[10px] leading-snug text-noc-faint">{t.note}</div>
                ) : (
                  <dl className="space-y-0.5">
                    {t.metrics.map((m) => (
                      <div
                        key={m.label}
                        className="flex items-center justify-between gap-2 text-[10px]"
                      >
                        <dt className="text-noc-faint">{m.label}</dt>
                        <dd
                          className={`font-mono ${
                            m.value === "—" ? "text-noc-faint" : "text-noc-muted"
                          }`}
                        >
                          {m.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── legenda (não depende só de cor: swatch + rótulo textual) ──
function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {CONN_HIST_LEGEND.map((it) => (
        <span key={it.kind} className="inline-flex items-center gap-1.5 text-[10px] text-noc-muted">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-3.5 rounded-[1px]"
            style={cellStyle(it.kind)}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ── sparkline de latência da Internet (rtt_avg por bucket); quebra em gaps ──
function InetLatencySparkline({ points }) {
  const segs = useMemo(() => {
    const vals = points.map((p) =>
      p && !p.gap && p.inet && typeof p.inet.rtt_avg === "number" && p.inet.rtt_avg >= 0
        ? p.inet.rtt_avg
        : null,
    );
    const present = vals.filter((v) => v != null);
    if (present.length < 2) return null;
    const min = Math.min(...present);
    const max = Math.max(...present);
    const span = Math.max(1, max - min);
    const W = 280;
    const H = 40;
    const out = [];
    let cur = [];
    vals.forEach((v, i) => {
      if (v == null) {
        if (cur.length) out.push(cur);
        cur = [];
        return;
      }
      const x = (i / Math.max(1, vals.length - 1)) * W;
      const y = H - 2 - ((v - min) / span) * (H - 4);
      cur.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    });
    if (cur.length) out.push(cur);
    return { out, min, max, W, H };
  }, [points]);

  if (!segs) return null;
  return (
    <div>
      <div className="mb-0.5 flex items-center justify-between text-[9px] uppercase tracking-[0.12em] text-noc-faint">
        <span>latência internet (ms)</span>
        <span className="font-mono normal-case text-noc-muted">
          {Math.round(segs.min)}–{Math.round(segs.max)} ms
        </span>
      </div>
      <svg
        viewBox={`0 0 ${segs.W} ${segs.H}`}
        className="h-10 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label="Tendência de latência da internet no período selecionado"
      >
        {segs.out.map((seg, i) => (
          <polyline
            key={i}
            fill="none"
            stroke="#2BD9F0"
            strokeWidth="1.5"
            strokeLinejoin="round"
            points={seg.join(" ")}
          />
        ))}
      </svg>
    </div>
  );
}

export default function ConnHistoryPanel({ deviceId }) {
  const [hours, setHours] = useState(CONN_HISTORY_DEFAULT_HOURS);
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [activeIdx, setActiveIdx] = useState(null);
  const seqRef = useRef(0);
  const acRef = useRef(null);

  const load = useCallback(() => {
    if (!deviceId) return;
    acRef.current?.abort();
    const ac = new AbortController();
    acRef.current = ac;
    const seq = ++seqRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    getConnHistory(deviceId, { hours, signal: ac.signal })
      .then((data) => {
        // resposta obsoleta (troca rápida de período) não sobrescreve a nova.
        if (shouldApplyResponse(seq, seqRef.current)) {
          setState({ loading: false, error: null, data });
        }
      })
      .catch((e) => {
        if (e?.name === "AbortError" || ac.signal.aborted) return;
        if (shouldApplyResponse(seq, seqRef.current)) {
          setState({ loading: false, error: e?.message || "Falha ao carregar", data: null });
        }
      });
  }, [deviceId, hours]);

  useEffect(() => {
    load();
    return () => acRef.current?.abort();
  }, [load]);

  // reseta o ponto em foco ao trocar device/período (evita índice fora de faixa).
  useEffect(() => {
    setActiveIdx(null);
  }, [deviceId, hours]);

  const { loading, error, data } = state;
  const points = data?.points || [];
  const summary = data?.summary || null;
  const activePoint =
    activeIdx != null && activeIdx >= 0 && activeIdx < points.length ? points[activeIdx] : null;

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <h3 className="section-label">Histórico de conectividade</h3>
        <p className="mt-0.5 text-[11px] text-noc-faint">
          Disponibilidade, latência e falhas observadas nos heartbeats.
        </p>
      </div>
      <PeriodSelector value={hours} onChange={setHours} disabled={loading && !data} />
    </div>
  );

  return (
    <section className="glass-card-pad space-y-3" aria-busy={loading}>
      {header}

      {/* Loading discreto (não bloqueia o resto do DeviceDetail). */}
      {loading && (
        <div className="py-6 text-center font-mono text-[11px] text-noc-muted">
          Carregando histórico de conectividade…
        </div>
      )}

      {/* Erro + tentar novamente (sem stack/URL/token/resposta bruta). */}
      {!loading && error && (
        <div className="space-y-2 py-4 text-center">
          <div className="font-mono text-[11px] text-noc-err">
            Não foi possível carregar o histórico de conectividade.
          </div>
          <button type="button" onClick={load} className="btn-ghost text-[11px]">
            Tentar novamente
          </button>
        </div>
      )}

      {/* Firmware sem suporte a conn (device legado). */}
      {!loading && !error && data && data.supported === false && (
        <div className="py-4 text-center">
          <div className="font-mono text-[11px] text-noc-muted">
            Histórico de conectividade não suportado por este firmware.
          </div>
          <div className="mx-auto mt-1 max-w-md text-[10px] text-noc-faint">
            O equipamento ainda não envia as medições de LAN, Internet e DNS necessárias para esta
            visualização.
          </div>
        </div>
      )}

      {/* Suportado, mas sem pontos no período. */}
      {!loading && !error && data && data.supported !== false && points.length === 0 && (
        <div className="py-6 text-center font-mono text-[11px] text-noc-muted">
          Nenhuma medição encontrada neste período.
        </div>
      )}

      {/* Dados. */}
      {!loading && !error && data && data.supported !== false && points.length > 0 && (
        <div className="space-y-3">
          {/* Resumo operacional. */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-lg border border-noc-accent/25 bg-noc-accent/5 px-3 py-2">
              <div className="text-[9px] uppercase tracking-[0.12em] text-noc-faint">
                Cobertura dos dados
              </div>
              <div className="mt-0.5 font-mono text-base font-semibold text-noc-accent">
                {summary ? formatPct(summary.coverage_pct) : "—"}
              </div>
              <div className="mt-0.5 text-[9px] text-noc-faint">
                {summary?.samples != null ? `${summary.samples} amostras` : "—"}
                {summary?.heartbeat_gap_buckets
                  ? ` · ${summary.heartbeat_gap_buckets} sem HB`
                  : ""}
              </div>
            </div>
            {TARGETS.map((t) => (
              <TargetSummaryCard
                key={t.key}
                label={t.label}
                tip={t.tip}
                target={summary ? summary[t.key] : null}
                fallbackOkPct={summary ? summary[`${t.key}_ok_pct`] : null}
              />
            ))}
          </div>

          {/* Gaps de heartbeat (contexto geral). */}
          {summary && (summary.gaps_gt_90s > 0 || summary.longest_heartbeat_gap_s > 0) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-noc-muted">
              <span>
                interrupções &gt; 90 s:{" "}
                <span className="font-mono text-noc-text2">{summary.gaps_gt_90s}</span>
              </span>
              <span>
                maior interrupção:{" "}
                <span className="font-mono text-noc-text2">
                  {Math.round((summary.longest_heartbeat_gap_s || 0) / 60)} min
                </span>
              </span>
            </div>
          )}

          {/* Timeline: 3 faixas alinhadas no mesmo eixo temporal. */}
          <div className="overflow-x-auto">
            <div className="min-w-[420px] space-y-1">
              {TARGETS.map((t) => (
                <TimelineLane
                  key={t.key}
                  label={t.label}
                  tip={t.tip}
                  targetKey={t.key}
                  points={points}
                  bucketMinutes={data.bucket_minutes}
                  activeIdx={activeIdx}
                  onActivate={setActiveIdx}
                />
              ))}
              {/* eixo temporal (início → fim, hora local). */}
              <div className="flex items-center gap-2 pt-0.5">
                <span className="w-16 shrink-0" />
                <div className="flex flex-1 justify-between font-mono text-[9px] text-noc-faint">
                  <span>{formatClockLocal(points[0]?.t)}</span>
                  <span>{formatClockLocal(points[points.length - 1]?.t)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Detalhe do ponto sob foco/mouse/toque. */}
          <DetailStrip point={activePoint} bucketMinutes={data.bucket_minutes} />

          {/* Latência (secundária). */}
          <InetLatencySparkline points={points} />

          {/* Legenda + ajuda. */}
          <div className="space-y-1.5 border-t border-noc-border/40 pt-2">
            <Legend />
            <p className="text-[10px] leading-snug text-noc-faint">
              <span className="text-noc-muted">Sem heartbeat</span> (hachura) = nenhum heartbeat no
              intervalo; <span className="text-noc-muted">Não reportado</span> (cinza) = houve
              heartbeat, mas aquele alvo não veio na amostra. {CONN_AVAILABILITY_HELP}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
